import { useId } from 'react';
import type { ShotOutcome } from '@golf/common';

/* Le tracé que ferait la balle, dessiné.
 *
 * « Slice » et « hook » ne disent rien à un débutant, et « socket » encore
 * moins. Une flèche courbée dit tout de suite ce qui s'est passé, et se
 * reconnaît d'un coup d'œil à bout de bras — ce qui est le vrai enjeu sur un
 * tapis de practice, où l'on tape en regardant sa balle.
 *
 * Convention du dessin : le joueur est en bas, la cible en haut. Le trait part
 * donc de la balle, posée au sol, et la pointe de flèche marque où elle finit.
 *
 * La pointe est un <marker> avec orient="auto" plutôt qu'un triangle placé à
 * la main : elle s'oriente seule sur la tangente du tracé, ce qui reste juste
 * si la courbe est retouchée. Son identifiant vient de useId(), sans quoi
 * plusieurs icônes sur le même écran partageraient le même marqueur.
 *
 * Pour un gaucher, tout ce qui est latéral s'inverse : le composant appelant
 * passe `mirror`, et une simple symétrie horizontale suffit — dessiner six
 * icônes de plus serait six occasions de se tromper.
 */

interface Props {
    outcome: ShotOutcome;
    /** Inverse la gauche et la droite (joueur gaucher). */
    mirror?: boolean;
    size?: number;
    className?: string;
}

interface Spec {
    /** Trajectoire principale. */
    d: string;
    /** Flèche au bout du tracé. */
    arrow?: boolean;
    /** Ligne de sol, qui donne son sens au dessin. */
    ground?: boolean;
    /** Marque de divot, pour la gratte. */
    divot?: boolean;
    /** Croix, pour le coup manqué. */
    cross?: boolean;
}

const SPECS: Record<ShotOutcome, Spec> = {
    // Droit sur la cible.
    center: { d: 'M12 20 L 12 6', arrow: true },
    // Part droit, puis s'échappe franchement à droite. La courbe finit
    // presque à l'horizontale : c'est ce mouvement latéral final qui se lit,
    // pas l'inclinaison générale.
    slice: { d: 'M12 21 C 12 15, 13 10, 20 8', arrow: true },
    // Miroir du slice.
    hook: { d: 'M12 21 C 12 15, 11 10, 4 8', arrow: true },
    // Socket : fuse à 45° dès la frappe, sans phase droite.
    shank: { d: 'M12 20 L 20 10', arrow: true },
    // Top : la balle file à ras de terre et court.
    top: { d: 'M12 20 C 13 19.4, 15 18.6, 20 18.4', arrow: true, ground: true },
    // Gratte : le club prend le sol avant la balle, qui monte mou et tombe court.
    fat: { d: 'M12 20 C 11.5 17, 12.5 15, 14 14.5', arrow: true, ground: true, divot: true },
    // Raté : la balle n'est pas partie.
    miss: { d: '', ground: true, cross: true },
};

/** Seules les fautes LATÉRALES s'inversent pour un gaucher.
 *
 *  Le jeu d'icônes mélange deux projections, et la ligne de sol dit laquelle :
 *  sans elle on regarde de dessus (la gauche et la droite sont des directions),
 *  avec elle on regarde de profil (la flèche va vers l'avant, et la hauteur est
 *  ce qui compte). Miroiter une vue de profil ferait partir la balle en arrière
 *  et poserait le divot du mauvais côté — pour un gaucher, un top part
 *  toujours devant. */
const LATERAL: ReadonlySet<ShotOutcome> = new Set(['slice', 'hook', 'shank']);

/** Les illustrations fournies, une par issue.
 *
 *  Servies en MASQUE et non en image : l'encre du fichier est sombre, le
 *  thème par défaut aussi, et une image telle quelle serait invisible. Un
 *  masque prend la couleur du texte — il suit donc les deux thèmes, l'état
 *  actif du bouton et le passage en couleur d'accent, sans second jeu de
 *  fichiers.
 *
 *  « Raté » n'en a pas : aucune illustration ne lui correspondait dans le
 *  lot fourni, et il garde le tracé dessiné plus bas. */
const ILLUSTRATIONS: Partial<Record<ShotOutcome, string>> = {
    center: '/icones/center.png',
    slice: '/icones/slice.png',
    hook: '/icones/hook.png',
    top: '/icones/top.png',
    fat: '/icones/fat.png',
    shank: '/icones/shank.png',
};

export function ShotIcon({ outcome, mirror = false, size = 28, className = '' }: Props) {
    const markerId = useId().replace(/:/g, '') + '-tip';
    const spec = SPECS[outcome];
    const flip = mirror && LATERAL.has(outcome);

    const illustration = ILLUSTRATIONS[outcome];
    if (illustration) {
        const mask = `url(${illustration}) center / contain no-repeat`;
        return (
            <span
                aria-hidden="true"
                className={className || undefined}
                style={{
                    display: 'inline-block',
                    width: className ? undefined : size,
                    height: className ? undefined : size,
                    backgroundColor: 'currentColor',
                    WebkitMask: mask,
                    mask,
                    // Le miroir vaut aussi pour les illustrations : un gaucher
                    // voit sa balle partir de l'autre côté, et une flèche qui
                    // pointe à l'envers dit le contraire de ce qui s'est passé.
                    transform: flip ? 'scaleX(-1)' : undefined,
                }}
            />
        );
    }

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className={className}
            style={flip ? { transform: 'scaleX(-1)' } : undefined}
        >
            {spec.arrow && (
                <defs>
                    {/* markerUnits="userSpaceOnUse" : sans cela la pointe est
                        dimensionnée en multiples de l'épaisseur du trait, soit
                        près du tiers de l'icône — elle avalait la courbe et
                        rendait hook et slice indiscernables. */}
                    <marker
                        id={markerId}
                        viewBox="0 0 6 6"
                        refX="4.5" refY="3"
                        markerWidth="6" markerHeight="6"
                        markerUnits="userSpaceOnUse"
                        orient="auto-start-reverse"
                    >
                        <path d="M 0.5 0.6 L 5.2 3 L 0.5 5.4 z" fill="currentColor" />
                    </marker>
                </defs>
            )}

            {spec.ground && (
                <line x1="5" y1="22" x2="19" y2="22"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.35" />
            )}

            {/* Divot : la motte arrachée juste avant la balle. */}
            {spec.divot && (
                <path d="M7 22 q 1.2 -2.2 2.6 -0.4"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.55" />
            )}

            {spec.d && (
                <path
                    d={spec.d}
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    markerEnd={spec.arrow ? `url(#${markerId})` : undefined}
                />
            )}

            {spec.cross && (
                <>
                    <line x1="8" y1="10" x2="16" y2="18" stroke="currentColor"
                        strokeWidth="2.2" strokeLinecap="round" />
                    <line x1="16" y1="10" x2="8" y2="18" stroke="currentColor"
                        strokeWidth="2.2" strokeLinecap="round" />
                </>
            )}

            {/* La balle, au point de frappe. */}
            <circle cx="12" cy="20.6" r="1.7" fill="currentColor" />
        </svg>
    );
}
